// errorBoundary.ts — tiny, dependency-free global crash recovery for the renderer.
//
// Stability is the weakest dimension: a single uncaught scene throw silently kills the rAF loop and
// leaves a dead canvas with no recovery and no telemetry. This module is the safety net:
//   - showRecoveryPanel(): swap #game for a clean "Something glitched — Reload" panel,
//   - reportFatal(): best-effort, PII-free telemetry (console.error only — there is no generic
//     error backend route; the board-report API is content-specific, so we don't repurpose it).
//
// It is deliberately framework-free (no Phaser import) so it works even if the engine itself failed
// to construct, and it self-guards against running twice (a crash can fire multiple listeners).

let recoveryShown = false;
let lastReportKey = '';

/** Best-effort fingerprint so we don't spam identical errors (e.g. an error that re-fires per frame). */
function fingerprint(err: unknown): string {
  if (err instanceof Error) return `${err.name}:${err.message}`;
  return String(err);
}

/**
 * Report a fatal error best-effort, PII-free. We only have message + stack here; there is no
 * dedicated error-ingest backend route, so this is console.error by design (the caller may add the
 * recovery panel separately). De-duped so a repeating error logs once, not every frame.
 */
export function reportFatal(context: string, err: unknown): void {
  const key = `${context}|${fingerprint(err)}`;
  if (key === lastReportKey) return;
  lastReportKey = key;
  // message + stack only — no URL params, no player input, no board content.
  const message = err instanceof Error ? err.message : String(err);
  const stack = err instanceof Error ? err.stack : undefined;
  console.error(`[trivia-munchers] fatal (${context}): ${message}`, stack ?? '');
}

/**
 * Replace the contents of the #game element (or a supplied parent) with a clean recovery panel.
 * Idempotent: a second call after the panel is up is a no-op so cascading errors don't stack panels.
 */
export function showRecoveryPanel(parent?: HTMLElement | null): void {
  if (recoveryShown) return;
  recoveryShown = true;
  const host = parent ?? document.getElementById('game');
  if (!host) return;

  // Wipe whatever is there (a frozen canvas) and build the panel with plain DOM: no engine, no deps.
  host.textContent = '';
  host.style.background = '#0a0c14';
  const wrap = document.createElement('div');
  wrap.setAttribute('role', 'alert');
  wrap.style.cssText = [
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'width:100%',
    'height:100%',
    'min-height:240px',
    'box-sizing:border-box',
    'padding:clamp(20px,5vw,48px)',
    'text-align:center',
    'font-family:"Fredoka",system-ui,-apple-system,"Segoe UI",sans-serif',
    'color:#e5e7eb',
    'background:radial-gradient(circle at 50% 22%, rgba(56,189,248,.16), transparent 34%), linear-gradient(rgba(64,84,111,.16) 1px, transparent 1px), linear-gradient(90deg, rgba(64,84,111,.13) 1px, transparent 1px), #0a0c14',
    'background-size:auto,80px 80px,80px 80px,auto',
  ].join(';');

  const panel = document.createElement('div');
  panel.style.cssText = [
    'position:relative',
    'width:min(620px,100%)',
    'box-sizing:border-box',
    'overflow:hidden',
    'border:1.5px solid #40546f',
    'border-radius:18px',
    'padding:34px 28px 30px',
    'background:#1b2233',
    'box-shadow:0 24px 80px rgba(0,0,0,.46),0 0 0 1px rgba(56,189,248,.08) inset',
  ].join(';');

  const rail = document.createElement('div');
  rail.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:4px;background:linear-gradient(90deg,#f59e0b,#27c281);';

  const badge = document.createElement('div');
  badge.style.cssText = [
    'width:76px',
    'height:76px',
    'margin:10px auto 22px',
    'border-radius:50%',
    'display:grid',
    'place-items:center',
    'border:2px solid rgba(255,77,109,.46)',
    'background:rgba(255,77,109,.12)',
    'color:#ff4d6d',
    'font-family:"Chakra Petch","Fredoka",system-ui,sans-serif',
    'font-size:34px',
    'font-weight:800',
    'line-height:1',
  ].join(';');
  badge.textContent = '!';

  const eyebrow = document.createElement('div');
  eyebrow.textContent = 'RECOVERY MODE';
  eyebrow.style.cssText = [
    'margin-bottom:8px',
    'font-family:"Chakra Petch","Fredoka",system-ui,sans-serif',
    'font-size:12px',
    'font-weight:700',
    'letter-spacing:.12em',
    'color:#38bdf8',
  ].join(';');

  const title = document.createElement('div');
  title.textContent = 'Something glitched';
  title.style.cssText = [
    'font-family:"Chakra Petch","Fredoka",system-ui,sans-serif',
    'font-size:clamp(24px,5vw,34px)',
    'font-weight:800',
    'line-height:1.05',
  ].join(';');

  const sub = document.createElement('div');
  sub.textContent = 'The game hit an unexpected error. Reloading keeps your local progress and starts a clean session.';
  sub.style.cssText = [
    'font-size:clamp(15px,3vw,17px)',
    'color:#8b93a7',
    'max-width:460px',
    'line-height:1.45',
    'margin:14px auto 0',
  ].join(';');

  const status = document.createElement('div');
  status.style.cssText = [
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'gap:10px',
    'margin:22px auto 24px',
    'max-width:430px',
    'min-height:44px',
    'padding:0 14px',
    'box-sizing:border-box',
    'border:1px solid rgba(64,84,111,.9)',
    'border-left:4px solid #27c281',
    'border-radius:12px',
    'background:#161b29',
    'color:#e5e7eb',
    'font-size:14px',
    'font-weight:650',
  ].join(';');
  const statusDot = document.createElement('span');
  statusDot.style.cssText = 'width:10px;height:10px;border-radius:50%;background:#27c281;box-shadow:0 0 18px rgba(39,194,129,.45);flex:0 0 auto;';
  const statusText = document.createElement('span');
  statusText.textContent = 'Local saves stay on this device.';
  status.append(statusDot, statusText);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = 'Reload';
  btn.style.cssText = [
    'appearance:none',
    'width:min(420px,100%)',
    'min-height:54px',
    'border:1px solid rgba(255,255,255,.14)',
    'cursor:pointer',
    'padding:14px 28px',
    'border-radius:12px',
    'font-family:"Chakra Petch","Fredoka",system-ui,sans-serif',
    'font-size:18px',
    'font-weight:800',
    'letter-spacing:.04em',
    'text-transform:uppercase',
    'color:#0a0c14',
    'background:#27c281',
    'box-shadow:0 0 0 5px rgba(39,194,129,.12)',
  ].join(';');
  btn.addEventListener('click', () => location.reload());

  panel.append(rail, badge, eyebrow, title, sub, status, btn);
  wrap.appendChild(panel);
  host.appendChild(wrap);
}

/** Show the recovery panel AND report the error — the one-call fatal handler for main.ts. */
export function handleFatal(context: string, err: unknown, parent?: HTMLElement | null): void {
  reportFatal(context, err);
  showRecoveryPanel(parent);
}

/** Test/eyes hook: has the recovery panel been shown? (Used to assert normal boot stays clean.) */
export function recoveryPanelShown(): boolean {
  return recoveryShown;
}
